/**
 * Disclosure rules for the report.
 *
 * Two different promises live here, and keeping them apart is the whole point:
 *
 *  - The anonymous segments — peers, direct reports, stakeholders — are pooled.
 *    No individual answer is ever visible, and a segment thin enough to point
 *    at a person is folded into the global figure.
 *  - The supervisor is *not* anonymous and never could be: every participant
 *    has exactly one, so a supervisor line is that person's own answer. It is
 *    shown deliberately, and the supervisor is told so before they answer.
 */
/** How the rater relates to the person they are evaluating. */
export const RATER_RELATIONSHIPS = [
    'SELF',
    'SUPERVISOR',
    'PEER',
    'DIRECT_REPORT',
    'STAKEHOLDER',
];
/**
 * Segments that are pooled and protected by the minimum-size rule.
 *
 * The supervisor is absent on purpose — it is a named line, not a pool.
 */
export const ANONYMOUS_SEGMENTS = ['PEER', 'DIRECT_REPORT', 'STAKEHOLDER'];
/** Everything a report can show as its own line. */
export const REPORTABLE_SEGMENTS = ['SUPERVISOR', ...ANONYMOUS_SEGMENTS];
/**
 * Whether the supervisor's answers count toward the global figure.
 *
 * Client decision: yes. The global is supervisor + peers + direct reports +
 * stakeholders — everyone who assessed the leader.
 */
export const SUPERVISOR_COUNTS_IN_GLOBAL = true;
/**
 * Whether the supervisor's assessment is shown on its own line.
 *
 * Client decision: yes. A leader should know what their manager said, and the
 * relationship is not one anonymity could protect anyway. The consequence is
 * that the supervisor's invitation and survey must say plainly that their
 * answers are attributed — see `survey.anonymityNoticeSupervisor` in the copy.
 * Turning this off restores full anonymity for every rater.
 */
export const SUPERVISOR_SHOWN_SEPARATELY = true;
/** Relationships whose answers make up the global figure. */
export const GLOBAL_RELATIONSHIPS = SUPERVISOR_COUNTS_IN_GLOBAL
    ? ['SUPERVISOR', ...ANONYMOUS_SEGMENTS]
    : ANONYMOUS_SEGMENTS;
/**
 * An anonymous segment is only shown on its own line when at least this many
 * distinct raters answered for it. Below the threshold its answers still count
 * towards the global figure — they are just never broken out.
 */
export const MIN_SEGMENT_SIZE = 3;
/**
 * How many anonymous segments must survive before any pooled breakdown is
 * shown.
 *
 * With a single anonymous segment next to the global figure, the rest is
 * "global minus that segment" — the breakdown would leak exactly what the
 * threshold exists to protect. The supervisor line does not count towards this:
 * it is a known quantity that can be subtracted out either way.
 */
export const MIN_VISIBLE_SEGMENTS = 2;
/**
 * Decide what a report may break out.
 *
 * Counts must be *distinct raters*, not answers: twelve answers from one peer
 * are one opinion. Use `countSegmentRaters` to build them.
 */
export const resolveSegmentDisclosure = (counts, options = {}) => {
    const reasons = {};
    if (options.forceGlobalOnly) {
        for (const segment of REPORTABLE_SEGMENTS)
            reasons[segment] = 'MANUAL_OVERRIDE';
        return {
            visible: [],
            folded: [...REPORTABLE_SEGMENTS],
            reasons,
            anonymousBreakdownSuppressed: true,
            globalOnly: true,
        };
    }
    const visible = [];
    const folded = [];
    // The supervisor line stands on its own rules: shown whenever they answered,
    // exempt from the minimum because it can never reach it.
    const supervisorCount = counts.SUPERVISOR ?? 0;
    if (!SUPERVISOR_SHOWN_SEPARATELY) {
        folded.push('SUPERVISOR');
        reasons.SUPERVISOR = 'NOT_DISCLOSED';
    }
    else if (supervisorCount === 0) {
        folded.push('SUPERVISOR');
        reasons.SUPERVISOR = 'NO_RESPONSES';
    }
    else {
        visible.push('SUPERVISOR');
    }
    const anonymousVisible = [];
    for (const segment of ANONYMOUS_SEGMENTS) {
        const count = counts[segment] ?? 0;
        if (count === 0) {
            reasons[segment] = 'NO_RESPONSES';
            folded.push(segment);
        }
        else if (count < MIN_SEGMENT_SIZE) {
            reasons[segment] = 'BELOW_MINIMUM';
            folded.push(segment);
        }
        else {
            anonymousVisible.push(segment);
        }
    }
    if (anonymousVisible.length < MIN_VISIBLE_SEGMENTS) {
        for (const segment of anonymousVisible) {
            folded.push(segment);
            reasons[segment] = 'TOO_FEW_SEGMENTS';
        }
        return {
            visible,
            folded,
            reasons,
            anonymousBreakdownSuppressed: true,
            globalOnly: visible.length === 0,
        };
    }
    visible.push(...anonymousVisible);
    return {
        visible,
        folded,
        reasons,
        anonymousBreakdownSuppressed: false,
        globalOnly: false,
    };
};
/** True when a pooled breakdown may be rendered at all. */
export const canBreakDownBySegment = (counts, options = {}) => !resolveSegmentDisclosure(counts, options).anonymousBreakdownSuppressed;
/**
 * True when this rater's answers are attributed rather than pooled, and so
 * must be told as much before they answer.
 */
export const isAttributedRater = (relationship) => relationship === 'SUPERVISOR' && SUPERVISOR_SHOWN_SEPARATELY;
/**
 * Minimum number of distinct raters before a report can be produced at all.
 *
 * Counted in people, not answers: one colleague answering twelve behaviors is
 * one opinion, and a report built on it would present that single person's view
 * as "how others see you".
 */
export const MIN_RESPONSES_FOR_REPORT = 3;
//# sourceMappingURL=confidentiality.js.map